import {FC, MouseEvent, useState} from 'react';
import {EvStationOutlined} from '@mui/icons-material';
import {Box, Button, Popover, Typography} from '@mui/material';
import {Link} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {ActionSheet, ActionSheetDestructive} from 'src/components/mobile/ActionSheet';
import {PhoneCardHead} from 'src/components/mobile/PhoneCardHead';
import {PhoneCardFacts} from 'src/components/mobile/PhoneCardFacts';
import {phonePillsRowSx} from 'src/components/mobile/mobileDesign.sx';
import {chargersApi} from '../../../api';
import {useAppDispatch} from '../../../app/hooks';
import {NotifyError} from '../../../utils/notification';
import {AppRouteEnum, AppRouteSpace} from '../../../utils/helper';
import {MenuButton} from '../../tables/respnsiveTable/MenuButton';
import {TableCard} from '../../tables/respnsiveTable/TableCard';
import {ChargePointDTO} from '../../../types';
import {StatusField} from '../../common/StatusField';
import {ConnectorStatusPill} from '../ConnectorStatusPill';
import {AppDispatch} from '../../../app/store';
import {preloaderChangeStatus} from '../../../stores/common/commonSlice';
import {chargerListCardSx, nameLinkSx} from './ChargerListCard.sx';
import {useCan} from 'src/permissions/hooks';

type DeleteChargerMutationTrigger = ReturnType<typeof chargersApi.useDeleteChargePointMutation>[0];

const handleDeleteCharger =
    (
        trigger: DeleteChargerMutationTrigger,
        dispatch: AppDispatch,
        businessId: number,
        chargerId: number
    ) =>
        async () => {
            dispatch(preloaderChangeStatus(true));
            try {
                await trigger({businessId, chargerId}).unwrap();
            } catch (error: any) {
                error?.data && NotifyError(error.data.message);
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const ChargerListCard: FC<ChargePointDTO> = ({
                                                        id,
                                                        chargePointId,
                                                        currentStatus,
                                                        connectors,
                                                        business,
                                                        location,
                                                        user,
                                                        canRemoveCharger,
                                                    }) => {
    const intl = useIntl();
    // The same permission the desktop row checks; the phone menu was offering it to anyone.
    const canDelete = useCan('chargers.delete');
    const [deleteMutation] = chargersApi.useDeleteChargePointMutation();
    const dispatch = useAppDispatch();
    // Below a desktop the menu is a sheet; from md up it stays the popover it has always been.
    const {isCompact} = useScreenTier();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // One node, drawn by whichever of the two the width calls for: the handler and the gate around
    // it are the same in both.
    const deleteAction = canDelete ? (
        <Button
            sx={chargerListCardSx.menuBtn}
            onClick={handleDeleteCharger(deleteMutation, dispatch, +business?.id, id)}
        >
            <Typography sx={chargerListCardSx.menuBtnText}>
                {intl.formatMessage({id: 'delete'})}
            </Typography>
        </Button>
    ) : null;

    return (
        <TableCard>
            <PhoneCardHead
                icon={<EvStationOutlined/>}
                title={(
                    <Box
                        component={Link}
                        to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.chargers, String(id)])}
                        sx={nameLinkSx}
                    >
                        {chargePointId}
                    </Box>
                )}
                subtitle={location?.name}
                action={canRemoveCharger && <MenuButton handleClick={handleClick}/>}
            />
            {/* The station's state, then its connector's: the two the columns carried. */}
            <Box sx={phonePillsRowSx}>
                <StatusField label={currentStatus}/>
                <ConnectorStatusPill connectors={connectors}/>
            </Box>
            <PhoneCardFacts
                facts={[
                    {
                        label: intl.formatMessage({id: 'business'}),
                        value: business?.name,
                    },
                    {
                        label: intl.formatMessage({id: 'locationNameColumnHeader'}),
                        value: location?.name,
                    },
                    {
                        label: intl.formatMessage({id: 'client'}),
                        value: user ? `${user?.firstName} ${user?.lastName}` : '-',
                    },
                ]}
            />
            {isCompact ? (
                <ActionSheet
                    open={open}
                    onClose={handleClose}
                    icon={<EvStationOutlined/>}
                    title={chargePointId}
                    subtitle={location?.name}
                >
                    <ActionSheetDestructive>{deleteAction}</ActionSheetDestructive>
                </ActionSheet>
            ) : (
                <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    sx={chargerListCardSx.popover}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    {deleteAction}
                </Popover>
            )}
        </TableCard>
    );
};
